import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Base from '../Base';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Card from '../../components/elements/Card';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo } from '../../utils';
import Icon from '../../components/common/Icon';

const Section = ({ icon, title, children }) => (
  <Card mx="3" mb="3" className="rules-section">
    <div className="rules-section__header">
      <span className="rules-section__icon">
        <Icon name={icon} theme="light" />
      </span>
      <h5 className="rules-section__title">{title}</h5>
    </div>
    {children}
  </Card>
);

// The screen where the rule is acted on, with where the team stands on it today.
const RuleLink = ({ to, icon, label, value }) => (
  <Link to={to} className="rules-link">
    <Icon name={icon} className="rules-link__icon" />
    <span className="rules-link__label">{label}</span>
    {value != null && <span className="rules-link__value">{value}</span>}
    <Icon name="angle-right" />
  </Link>
);

const Steps = ({ steps }) => (
  <ol className="rules-tiebreak mb-2">
    {steps.map((step, idx) => (
      <li key={idx} className="rules-tiebreak__item">
        <span className="rules-tiebreak__number">{idx + 1}</span>
        <span>{step}</span>
      </li>
    ))}
  </ol>
);

const ScoringRulesPage = () => {
  const { t } = useTranslation();

  const [scoringInfo, setScoringInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [points, setPoints] = useState([]);

  useEffect(() => {
    fetchUserInfo('/scoring-rules', setScoringInfo, setLoading, setError, []);
    fetchUserInfo('/points', setPoints, () => {}, () => {}, []);
  }, []);

  const showLadies =
    Array.isArray(points) && points.some((row) => Number(row?.ladies ?? 0) > 0);
  const hasPoints = Array.isArray(points) && points.length > 0;
  const myTeam = scoringInfo?.my_team;

  return (
    <Base activeNav="home">
      <Back to="/home" />
      <Title>
        <Icon name="whistle" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('scoringRules.title')}
      </Title>

      <Section icon="gift" title={t('scoringRules.readinessClaimTitle')}>
        <p className="mb-2">{t('scoringRules.readinessClaimIntro')}</p>
        <p className="mb-0 text-secondary" style={{ fontSize: '0.88rem' }}>
          <Icon name="lock" theme="primary" className="me-1" />
          {t('scoringRules.readinessClaimNote')}
        </p>
        {myTeam && (
          <div className="rules-links">
            <RuleLink
              to="/bonus-points"
              icon="gift"
              label={t('scoringRules.claimLink')}
              value={myTeam.readiness_to_claim > 0
                ? t('scoringRules.toClaim', { count: myTeam.readiness_to_claim })
                : null}
            />
            <RuleLink
              to="/team-readiness"
              icon="list-check"
              label={t('teamReadiness')}
              value={myTeam.readiness_percent != null ? `${myTeam.readiness_percent}%` : null}
            />
          </div>
        )}
      </Section>

      {scoringInfo?.max_challenge_others > 0 && (
        <Section icon="megaphone" title={t('scoringRules.challengesTitle')}>
          <p className="mb-2">
            {t('scoringRules.challengePoints', {
              max: scoringInfo.max_challenge_others,
              challengePoints: scoringInfo.challenge_points ?? 1,
            })}
          </p>
          <Steps
            steps={[
              t('scoringRules.rivalStepOpen'),
              t('scoringRules.rivalStepPick'),
              t('scoringRules.rivalStepTap', { button: t('challengeOther') }),
            ]}
          />
          {myTeam && (
            <p className="mb-0">
              <strong>
                {t('scoringRules.rivalsCount', {
                  count: myTeam.rivals_count,
                  max: scoringInfo.max_challenge_others,
                })}
              </strong>
            </p>
          )}
          <div className="rules-links">
            <RuleLink
              to="/teams"
              icon="search"
              label={t('scoringRules.findRivals')}
              value={myTeam ? `${myTeam.rivals_count}/${scoringInfo.max_challenge_others}` : null}
            />
          </div>
        </Section>
      )}

      <Section icon="hundred-points" title={t('points')}>
        <p className="mb-2">{t('scoringRules.pointsTableIntro')}</p>
        <p className="mb-2 text-secondary" style={{ fontSize: '0.88rem' }}>
          <Icon name="star" theme="primary" className="me-1" />
          {t('scoringRules.jokerExplain')}
        </p>
        <p className="mb-3">
          {t('scoringRules.jokerHowTo', { button: t('chooseJoker') })}
          {myTeam && (
            <>
              {' '}
              <strong>
                {myTeam.joker_name
                  ? t('scoringRules.yourJoker', { name: myTeam.joker_name })
                  : t('scoringRules.noJokerYet')}
              </strong>
            </>
          )}
        </p>

        {hasPoints ? (
          <div className="rules-table">
            <table className="table table-borderless rules-table__table">
              <thead>
                <tr>
                  <th>{t('rank')}</th>
                  <th>{t('points')}</th>
                  <th>
                    <Icon name="star" theme="primary" style={{ fontSize: '0.8rem' }} pack="sr" />{' '}
                    {t('joker')}
                  </th>
                  {showLadies && (
                    <th>
                      <Icon
                        name="woman-head"
                        style={{ color: '#FF6ec7', fontSize: '0.8rem' }}
                        pack="sr"
                      />{' '}
                      {t('ladies')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {points.map((row) => (
                  <tr key={row.rank}>
                    <td>
                      <span
                        className={`rank-badge ${
                          row.rank === 1
                            ? 'rank-badge--gold'
                            : row.rank === 2
                            ? 'rank-badge--silver'
                            : row.rank === 3
                            ? 'rank-badge--bronze'
                            : ''
                        }`}
                        style={{ width: '1.5rem', height: '1.5rem', fontSize: '0.75rem' }}
                      >
                        {row.rank}
                      </span>
                    </td>
                    <td><strong>{row.score}</strong></td>
                    <td>{row.joker || 0}</td>
                    {showLadies && <td>{row.ladies || 0}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mb-0 text-secondary" style={{ fontSize: '0.88rem' }}>
            <Icon name="clock" theme="primary" className="me-1" />
            {t('scoringRules.pointsNotSet')}
          </p>
        )}

        <div className="rules-links">
          <RuleLink
            to="/battles"
            icon="star"
            label={t('scoringRules.pickJoker')}
            value={myTeam?.joker_name || null}
          />
        </div>
      </Section>

      {scoringInfo?.picture_missions?.length > 0 && (
        <Section icon="camera" title={t('scoringRules.pictureMissionsTitle')}>
          <p className="mb-2">{t('scoringRules.pictureMissionsIntro')}</p>
          <p className="mb-3 text-secondary" style={{ fontSize: '0.88rem' }}>
            <Icon name="check-circle" theme="primary" className="me-1" />
            {t('scoringRules.pictureMissionsApproval')}
          </p>

          <div className="rules-table">
            <table className="table table-borderless rules-table__table">
              <thead>
                <tr>
                  <th>{t('mission')}</th>
                  <th>{t('points')}</th>
                </tr>
              </thead>
              <tbody>
                {scoringInfo.picture_missions.map((m) => (
                  <tr key={m.id}>
                    <td><strong>{m.name}</strong></td>
                    <td>
                      <span className="points-chip points-chip--earned" style={{ marginTop: 0 }}>
                        <Icon name="star" />
                        +{m.points}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rules-links">
            <RuleLink to="/missions" icon="camera" label={t('viewMissions')} />
          </div>
        </Section>
      )}

      {scoringInfo?.quizzes?.length > 0 && (
        <Section icon="clipboard-list" title={t('scoringRules.quizzesTitle')}>
          <p className="mb-3">{t('scoringRules.quizzesIntro')}</p>

          {scoringInfo.quizzes.map((quiz) => (
            <div key={quiz.id} className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{quiz.name}</strong>
                <span className="points-chip points-chip--earned" style={{ marginTop: 0 }}>
                  <Icon name="star" />
                  +{quiz.total_points}
                </span>
              </div>

              {quiz.questions?.length > 0 && (
                <div className="rules-table">
                  <table className="table table-borderless rules-table__table">
                    <thead>
                      <tr>
                        <th>{t('question')}</th>
                        <th>{t('points')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quiz.questions.map((q) => (
                        <tr key={q.order}>
                          <td><strong>{t('question')} {q.order}</strong></td>
                          <td>
                            <span className="points-chip points-chip--earned" style={{ marginTop: 0 }}>
                              <Icon name="star" />
                              +{q.points}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {scoringInfo?.has_votes && scoringInfo.vote_thresholds?.length > 0 && (
        <Section icon="party-horn" title={t('scoringRules.votesTitle')}>
          <p className="mb-2">
            {t('scoringRules.votesIntro', { points: scoringInfo.vote_bonus_points || 1 })}
          </p>

          <div className="rules-table">
            <table className="table table-borderless rules-table__table">
              <thead>
                <tr>
                  <th>{t('scoringRules.votesReceived')}</th>
                  <th>{t('scoringRules.bonusPoints')}</th>
                </tr>
              </thead>
              <tbody>
                {scoringInfo.vote_thresholds.map((threshold) => (
                  <tr key={threshold}>
                    <td><strong>{threshold}</strong></td>
                    <td>
                      <span className="points-chip points-chip--earned" style={{ marginTop: 0 }}>
                        <Icon name="star" />
                        +{scoringInfo.vote_bonus_points || 1}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rules-links">
            <RuleLink to="/cheers" icon="share" label={t('shareYourTeam')} />
          </div>
        </Section>
      )}

      {scoringInfo?.battles?.length > 0 && (
        <Section icon="balance-scale-right" title={t('scoringRules.tiebreakTitle')}>
          <p className="mb-2">{t('scoringRules.tiebreakIntro')}</p>
          <ol className="rules-tiebreak">
            {scoringInfo.battles.map((challenge, idx) => (
              <li key={idx} className="rules-tiebreak__item">
                <span className="rules-tiebreak__number">{idx + 1}</span>
                <span>{challenge}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}
    </Base>
  );
};

export default ScoringRulesPage;
